import React from "react";
import { ConfigProvider, Form, Select } from "antd";
import { createUseStyles } from "react-jss";


const { Option } = Select;

const UIFormSelectInput = ({
  label,
  name,
  required,
  rules,
  onChange,
  validateStatus,
  hasFeedback,
  help,
  disabled,
  defaultValue,
  placeholder = "",
  option = [],
  mode,
  style,
  source,
  compact = false,
}) => {
  const styles = useStyles();
  const normalizedDefaultValue = Array.isArray(defaultValue)
    ? defaultValue.map((value) => value?.toString())
    : defaultValue?.toString();

  return (
    <ConfigProvider
      theme={
        source === "CandidateSignUp"
          ? {
              components: {
                Select: {
                  selectorBg: "#f8fbff",
                  colorBorder: "#d8e4ef",
                  hoverBorderColor: "#0c64ae",
                  activeBorderColor: "#0c64ae",
                  activeOutlineColor: "rgba(12, 100, 174, 0.1)",
                  borderRadius: 10,
                  controlHeight: 46,
                },
              },
            }
          : undefined
      }
    >
      <Form.Item
        label={<span style={{ fontSize: compact ? "14px" : "16px", fontWeight: "500" }}>{label}</span>}
        name={name}
        required={required}
        rules={rules}
        hasFeedback={hasFeedback}
        help={help}
        validateStatus={validateStatus}
        initialValue={normalizedDefaultValue}
      >
        <Select
          mode={mode}
          className={
            compact
              ? styles.compactSelect
              : source === "EmployerInfo" || source === "employer"
                ? `employer-form-select ${styles.empInputStyle}`
                : source === "CandidateSignUp"
                  ? `candidate-auth-select ${styles.candidateAuthSelect}`
                  : styles.customSelect
          }
          style={{ fontSize: compact ? 14 : 16, fontWeight: "500", width: "100%", ...style }}
          showSearch
          onChange={onChange}
          disabled={disabled}
          placeholder={placeholder}
          allowClear
          filterOption={(val, option) =>
            option.children.toString().toLowerCase().indexOf(val.toLowerCase()) >=
            0
          }
        >
          {option &&
            Object.values(option)?.map((item, index) => {
              return (
                <Option key={index} value={Object.keys(option)[index]}>
                  {item}
                </Option>
              );
            })}
        </Select>
      </Form.Item>
    </ConfigProvider>
  );
};

export default UIFormSelectInput;

const useStyles = createUseStyles({
  "@global": {
    ".employer-form-select.ant-select": {
      width: "100% !important",
      height: "42px !important",
    },
    ".employer-form-select.ant-select .ant-select-selector": {
      width: "100% !important",
      height: "42px !important",
      minHeight: "42px !important",
      padding: "0 12px !important",
      display: "flex !important",
      alignItems: "center !important",
      border: "1px solid #e5e7eb !important",
      borderRadius: "22px !important",
      boxShadow: "0 12px 35px rgba(15, 23, 42, 0.04) !important",
    },
    ".employer-form-select.ant-select .ant-select-selection-item, .employer-form-select.ant-select .ant-select-selection-placeholder": {
      lineHeight: "40px !important",
      fontSize: "16px !important",
      fontWeight: "500 !important",
    },
    ".employer-form-select.ant-select:hover .ant-select-selector, .employer-form-select.ant-select-focused .ant-select-selector": {
      borderColor: "#e5e7eb !important",
      boxShadow: "0 12px 35px rgba(15, 23, 42, 0.04) !important",
    },
    ".candidate-auth-select.ant-select": {
      width: "100% !important",
      height: "46px !important",
      marginBottom: "0 !important",
    },
    ".candidate-auth-select.ant-select .ant-select-selector": {
      width: "100% !important",
      height: "46px !important",
      minHeight: "46px !important",
      padding: "0 12px !important",
      display: "flex !important",
      alignItems: "center !important",
      border: "1px solid #d8e4ef !important",
      borderRadius: "10px !important",
      background: "#f8fbff !important",
      backgroundColor: "#f8fbff !important",
      boxShadow: "none !important",
    },
    ".candidate-auth-select.ant-select:hover .ant-select-selector": {
      borderColor: "#0c64ae !important",
    },
    ".candidate-auth-select.ant-select-focused .ant-select-selector": {
      borderColor: "#0c64ae !important",
      boxShadow: "0 0 0 3px rgba(12, 100, 174, 0.1) !important",
    },
    ".candidate-auth-select.ant-select .ant-select-selection-item, .candidate-auth-select.ant-select .ant-select-selection-placeholder": {
      lineHeight: "44px !important",
      color: "#334e68 !important",
      fontSize: "15px !important",
      fontWeight: "400 !important",
    },
  },
  candidateAuthSelect: {
    width: "100% !important",
    height: "46px !important",
    marginBottom: "0 !important",
    "& > .ant-select-selector, &.ant-select.ant-select-outlined > .ant-select-selector": {
      width: "100% !important",
      height: "46px !important",
      minHeight: "46px !important",
      padding: "0 12px !important",
      display: "flex !important",
      alignItems: "center !important",
      border: "1px solid #d8e4ef !important",
      borderRadius: "10px !important",
      background: "#f8fbff !important",
      backgroundColor: "#f8fbff !important",
      boxShadow: "none !important",
    },
    "&:hover > .ant-select-selector": {
      borderColor: "#0c64ae !important",
    },
    "&.ant-select-focused > .ant-select-selector": {
      borderColor: "#0c64ae !important",
      boxShadow: "0 0 0 3px rgba(12, 100, 174, 0.1) !important",
    },
    "& .ant-select-selection-item, & .ant-select-selection-placeholder": {
      lineHeight: "44px !important",
      color: "#334e68",
      fontSize: "15px !important",
      fontWeight: "400 !important",
    },
  },
  compactSelect: {
    width: "100% !important",
    height: "42px !important",
    marginBottom: 0,
    border: "1px solid #e5e7eb !important",
    borderRadius: "22px !important",
    background: "#ffffff !important",
    boxShadow: "0 8px 22px rgba(15, 23, 42, 0.04) !important",
    overflow: "hidden",
    '&:focus-within': {
      borderColor: "#0C64AE !important",
      boxShadow: "0 8px 22px rgba(12, 100, 174, 0.08) !important",
    },
    '& > .ant-select-selector, &.ant-select.ant-select-outlined > .ant-select-selector': {
      width: "100% !important",
      height: "42px !important",
      minHeight: "42px !important",
      padding: "0 12px !important",
      borderRadius: "22px !important",
      display: "flex !important",
      alignItems: "center !important",
      fontSize: "14px !important",
      border: "0 !important",
      boxShadow: "none !important",
      background: "transparent !important",
    },
    '& .ant-select-selection-item, & .ant-select-selection-placeholder': {
      lineHeight: "40px !important",
      fontSize: "14px !important",
    },
  },
  empInputStyle: {
    padding: "12px",
    fontWeight: "500",
    border: "1px solid #e5e7eb",
    borderRadius: 22,
    height: 42,
    display: "flex",
    alignItems: "center",
    boxShadow: "0 12px 35px rgba(15, 23, 42, 0.04)",
  },
  customSelect: {
    marginBottom: 10,
    '& .ant-select-selector': {
      minHeight: 50,
      height: 'auto',
      padding: '6px 10px',
      fontSize: 16,
      fontWeight: 500,
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
    },
    '&.ant-select-multiple .ant-select-selection-overflow': {
      flexWrap: 'wrap',
      maxWidth: '100%',
    },
    '&.ant-select-multiple .ant-select-selection-overflow-item': {
      maxWidth: '100%',
    },
    '& .ant-select-selection-item': {
      fontSize: 16,
      fontWeight: 500,
    },
    '& .ant-select-selection-placeholder': {
      fontSize: 16,
      fontWeight: 500,
    },
  },
});
